import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import authApi from '../api/auth/authApi';
import userApi from '../api/user/userApi';
import { eventApi } from '../api/events/eventApi';
import { STORAGE_KEYS } from '../api/config';

// Helper to check if event is marked default
const isEventDefault = (evt, userData) => {
  if (!evt) return false;
  if (evt.is_default === 1 || evt.is_default === '1' || evt.is_default === true) return true;
  if (evt.pivot?.is_default === 1 || evt.pivot?.is_default === '1' || evt.pivot?.is_default === true) return true;
  if (userData?.default_event_id && String(evt.id) === String(userData.default_event_id)) return true;
  return false;
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (storedUser && token) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        const userEvents = Array.isArray(userData.events) ? userData.events : [];
        if (userEvents.length > 0) {
          setEvents(userEvents);
          
          const savedEventId = localStorage.getItem('selectedEventId');
          const savedDefaultId = localStorage.getItem('defaultEventId');

          let activeEvent = null;
          if (savedEventId) {
            activeEvent = userEvents.find(
              e => String(e.id) === String(savedEventId) || e.event_address === savedEventId
            );
          }
          if (!activeEvent && savedDefaultId) {
            activeEvent = userEvents.find(
              e => String(e.id) === String(savedDefaultId) || e.event_address === savedDefaultId
            );
          }
          if (!activeEvent) {
            activeEvent = userEvents.find(e => isEventDefault(e, userData));
          }

          const finalEvent = activeEvent || userEvents[0] || null;
          setCurrentEvent(finalEvent);
          if (finalEvent) {
            localStorage.setItem(STORAGE_KEYS.UID, finalEvent.event_address);
            localStorage.setItem(STORAGE_KEYS.EVENT_ID, finalEvent.id);
          }
        }
      } catch (err) {
        console.error('Error parsing stored user:', err);
      }
    }
    setLoading(false);
  }, []);

  const fetchProfile = async (id) => {
    try {
      const response = await userApi.getProfile(id || user?.id);
      if (response.success || response.status === 800) {
        let updatedEvents = [];
        try {
          const eventsResponse = await eventApi.getUserEvents();
          updatedEvents = eventsResponse?.data?.data || eventsResponse?.data || [];
        } catch (eventErr) {
          console.error('Error fetching events during profile refresh:', eventErr);
        }

        const updatedUser = { ...user, ...response.data };
        if (updatedEvents.length > 0) {
          updatedUser.events = updatedEvents;
          setEvents(updatedEvents);

          const savedEventId = localStorage.getItem('selectedEventId');
          const currentExist = updatedEvents.find(
            e => String(e.id) === String(savedEventId) || e.event_address === savedEventId
          );
          if (!currentExist && updatedEvents.length > 0) {
            const defaultEvt = updatedEvents.find(e => isEventDefault(e, updatedUser)) || updatedEvents[0];
            setCurrentEvent(defaultEvt);
            localStorage.setItem('selectedEventId', defaultEvt.id?.toString());
            localStorage.setItem(STORAGE_KEYS.UID, defaultEvt.event_address);
            localStorage.setItem(STORAGE_KEYS.EVENT_ID, defaultEvt.id);
          } else if (currentExist) {
            setCurrentEvent(currentExist);
          }
        }

        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.login(credentials);
      
      if (response.success || response.status === 800) {
        const { token, user_data, events: userEventsData } = response.data;
        const userEvents = Array.isArray(userEventsData) ? userEventsData : (Array.isArray(user_data?.events) ? user_data.events : []);
        
        // Clear any old user's leftover event storage keys
        localStorage.removeItem('selectedEventId');
        localStorage.removeItem('defaultEventId');
        localStorage.removeItem('defaultEvent');
        localStorage.removeItem('selectedEvent');

        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user_data));
        localStorage.setItem(STORAGE_KEYS.USER_ID, user_data.id);
        
        if (Array.isArray(userEvents) && userEvents.length > 0) {
          // Find default event where is_default === 1 or pivot.is_default === 1
          const defaultEvt = userEvents.find(e => isEventDefault(e, user_data)) || userEvents[0];

          localStorage.setItem(STORAGE_KEYS.UID, defaultEvt.event_address);
          localStorage.setItem(STORAGE_KEYS.EVENT_ID, defaultEvt.id);
          localStorage.setItem('selectedEventId', defaultEvt.id?.toString());
          localStorage.setItem('defaultEventId', defaultEvt.id?.toString());
          localStorage.setItem('defaultEvent', JSON.stringify(defaultEvt));

          setEvents(userEvents);
          setCurrentEvent(defaultEvt);
        } else {
          setEvents([]);
          setCurrentEvent(null);
        }
        
        setUser(user_data);
        toast.success('Login successful!');
        
        navigate('/dashboard');
        return response;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err) {
      const message = err.message || 'Login failed';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Purge all keys so multi-account sessions do not bleed into each other
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.UID);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.EVENT_ID);
      localStorage.removeItem('selectedEventId');
      localStorage.removeItem('defaultEventId');
      localStorage.removeItem('defaultEvent');
      localStorage.removeItem('selectedEvent');

      setUser(null);
      setEvents([]);
      setCurrentEvent(null);
      toast.info('Logged out successfully');
      navigate('/login');
    }
  };

  const setDefaultEvent = async (eventOrId) => {
    try {
      const targetId = typeof eventOrId === 'object' ? eventOrId?.id : eventOrId;
      const targetEvent = events.find(e => e.id === parseInt(targetId)) || (typeof eventOrId === 'object' ? eventOrId : null);

      if (!targetId) throw new Error('Invalid event ID');

      // 1. Call API POST /api/set-default-event
      const response = await eventApi.setDefaultEvent(targetId);

      // 2. Update Local Storage for active user session
      localStorage.setItem('defaultEventId', targetId.toString());
      if (targetEvent) {
        localStorage.setItem('defaultEvent', JSON.stringify(targetEvent));
        localStorage.setItem('selectedEventId', targetId.toString());
        localStorage.setItem(STORAGE_KEYS.UID, targetEvent.event_address);
        localStorage.setItem(STORAGE_KEYS.EVENT_ID, targetId);
        setCurrentEvent(targetEvent);
      }

      // 3. Update User State & Events List
      if (Array.isArray(events)) {
        const updatedEvents = events.map(e => ({
          ...e,
          is_default: String(e.id) === String(targetId) ? 1 : 0
        }));
        setEvents(updatedEvents);
      }

      if (user) {
        const updatedUser = { ...user, default_event_id: targetId };
        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
      }

      const eventTitle = targetEvent?.event_title || targetEvent?.name || 'Shughuli';
      toast.success(`${eventTitle} set as default event!`);
      return response;
    } catch (err) {
      console.error('Error setting default event:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Imeshindwa kuweka shughuli ya msingi');
      throw err;
    }
  };

  const changePassword = async (passwordData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.changePassword(passwordData);
      if (response.success || response.status === 800) {
        toast.success('Password changed successfully!');
        return response;
      } else {
        throw new Error(response.message || 'Failed to change password');
      }
    } catch (err) {
      const message = err.message || 'Failed to change password';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const userId = user?.id || localStorage.getItem(STORAGE_KEYS.USER_ID);
      if (!userId) throw new Error('User ID not found');
      
      const response = await userApi.updateProfile(userId, profileData);
      
      if (response.success || response.status === 800) {
        const updatedUser = { ...user, ...profileData };
        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        toast.success('Profile updated successfully!');
        return response;
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (err) {
      const message = err.message || 'Failed to update profile';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccountRequest = async (data) => {
    setLoading(true);
    try {
      const response = await userApi.requestDeleteOtp(data);
      return response;
    } catch (err) {
      toast.error(err.message || 'Failed to request deletion OTP');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccountVerify = async (data) => {
    setLoading(true);
    try {
      const response = await userApi.deleteAccount(data);
      if (response.success || response.status === 800) {
        logout();
      }
      return response;
    } catch (err) {
      toast.error(err.message || 'Failed to delete account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const switchEvent = (event) => {
    setCurrentEvent(event);
    localStorage.setItem('selectedEventId', event.id?.toString() || event.event_address);
    localStorage.setItem(STORAGE_KEYS.UID, event.event_address);
    localStorage.setItem(STORAGE_KEYS.EVENT_ID, event.id);
    
    // Hard refresh so all components & state reflect the newly selected event
    window.location.reload();
  };

  return {
    user,
    events,
    currentEvent,
    loading,
    error,
    login,
    logout,
    switchEvent,
    setDefaultEvent,
    changePassword,
    updateProfile,
    fetchProfile,
    deleteAccountRequest,
    deleteAccountVerify,
    isAuthenticated: !!user,
    isAdmin: user?.role === 1,
    isUser: user?.role === 0,
    updateUser: setUser
  };
};
